import {
  type GoldRushClientSettings,
  type GoldRushResponse,
} from "../types/Generic.types";
import { debugOutput } from "./debugOutput";
import pLimit from "p-limit";

export class Execution {
  private maxRetries: number;
  private retryDelay: number;
  private enableRetry: boolean;
  private processes: pLimit.Limit;

  constructor(
    private settings: GoldRushClientSettings,
    private headers: Record<string, string>
  ) {
    this.processes = pLimit(this.settings.threadCount ?? 2);
    this.maxRetries = settings.maxRetries ?? 2;
    this.retryDelay = settings.retryDelay ?? 1500;
    this.enableRetry = settings.enableRetry ?? true;
  }

  async execute<T>(
    endpoint: URL,
    parseData: (data: GoldRushResponse<T>) => GoldRushResponse<T>
  ): Promise<GoldRushResponse<T>> {
    let retryCount: number = 0;
    let completed: boolean = false;

    while (!completed) {
      try {
        const startTime: Date = new Date();

        const response = await this.processes(() =>
          fetch(endpoint, {
            headers: this.headers,
          })
        );

        debugOutput(
          this.settings,
          `Request URL: ${
            response ? new URL(response.url) : endpoint
          } | Response code: ${
            response ? response.status : 429
          } | Response time: ${(
            new Date().getTime() - startTime.getTime()
          ).toFixed(2)}ms`
        );

        if (response.status === null || response.status === 429) {
          throw new Error(`Received status code: ${response.status ?? 429}`);
        }

        const data = (await response.json()) as GoldRushResponse<T>;

        if (data.error) {
          throw data;
        }

        const parsedData = parseData(data);
        completed = true;

        return {
          data: parsedData.data as T,
          error: false,
          error_code: null,
          error_message: null,
        };
      } catch (error: any) {
        debugOutput(
          this.settings,
          `Request URL: ${endpoint} | Retry Enabled: ${this.enableRetry} | Retry Count: ${retryCount} | Max Retries: ${this.maxRetries} | Retry Delay: ${this.retryDelay}ms`,
          error
        );

        if (
          this.enableRetry &&
          (error?.error_code === 429 || error?.cause?.code === 429)
        ) {
          if (retryCount >= this.maxRetries) {
            completed = true;
            return {
              data: null,
              error: true,
              error_code: 429,
              error_message: `${this.maxRetries} retries exceeded`,
            };
          } else {
            retryCount++;
            await new Promise((resolve) =>
              setTimeout(resolve, this.retryDelay)
            );
            completed = false;
          }
        } else {
          completed = true;
          return {
            data: null,
            error: true,
            error_code: error?.cause?.code || error?.error_code || 500,
            error_message:
              error?.cause?.message ||
              error?.error_message ||
              "Internal server error",
          };
        }
      }
    }

    return {
      data: null,
      error: true,
      error_code: 500,
      error_message: "Internal server error",
    };
  }
}
